/**
 * ASC 606 Phase 9g — Step 3 Tracker
 * ==================================
 *
 * Cross-contract operational view of Step 3 lifecycle records:
 *   • Variable Consideration — ACTIVE components across all contracts; re-assess
 *     in bulk or per-contract; shows constraint status + pending adjustment.
 *   • Refund Liabilities — accrue / settle / release across contracts (ASC
 *     606-10-32-10). Complements the per-contract view inside RevenueReviews.
 *   • CPS — consideration payable to customer, both 32-26 branches, full
 *     lifecycle. Complements the per-contract create+action buttons in review.
 *
 * The RevenueReviews modal is the per-contract workflow surface; this tracker
 * is the cross-contract ops surface (reviewer asks "show me all ACCRUED refund
 * liabilities" or "which contracts have unreviewed material reassessment deltas").
 */

import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch, formatAmount, formatAmountCompact } from '../constants';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import TabBar from '../components/ui/TabBar';
import DataTable, { Column } from '../components/ui/DataTable';

const API = `${API_V1}/revenue`;

// ── Types ─────────────────────────────────────────────────

interface VCComponent {
  id: number;
  component_type: string;
  estimation_method: string;
  estimated_amount: number;
  probability: number | null;
  constraint_applied: boolean;
  constrained_amount: number;
  pending_adjustment_amount: number;
  status: string;
  rationale: string | null;
  last_reassessed_at: string | null;
}

interface VCContractRow {
  contract_id: number;
  contract_number: string;
  total_value: number;
  variable_consideration_aggregate: number;
  constraint_probability_threshold: number | null;
  has_components: boolean;
  has_unquantified_components: boolean;
  components: VCComponent[];
}

interface RefundLiability {
  id: number;
  revenue_contract_id: number;
  entity_code: string | null;
  initial_amount: number;
  accrued_amount: number;
  settled_amount: number;
  released_amount: number;
  liability_balance: number;
  currency: string;
  status: 'OPEN' | 'ACCRUED' | 'SETTLED' | 'RELEASED';
  created_at: string | null;
  notes: string | null;
}

interface CPSRow {
  id: number;
  revenue_contract_id: number;
  entity_code: string | null;
  payment_type: string;
  classification: 'DISTINCT_SERVICE_RECEIVED' | 'TRANSACTION_PRICE_REDUCTION';
  description: string;
  gross_amount: number;
  distinct_service_fair_value: number | null;
  expense_amount: number;
  tp_reduction_amount: number;
  balance_due: number;
  status: 'OPEN' | 'ACCRUED' | 'SETTLED' | 'RELEASED';
  currency: string;
  created_at: string | null;
}

interface ContractSlim { id: number; contract_number: string; customer_name: string | null; }

// ── Helpers ───────────────────────────────────────────────

const statusColor = (s: string) =>
  s === 'OPEN' ? 'var(--warning)'
  : s === 'ACCRUED' ? 'var(--accent)'
  : s === 'SETTLED' ? 'var(--positive)'
  : s === 'RELEASED' ? 'var(--text-secondary)'
  : 'var(--text-primary)';

const statusBg = (s: string) =>
  s === 'OPEN' ? 'rgba(234,179,8,0.15)'
  : s === 'ACCRUED' ? 'rgba(59,130,246,0.15)'
  : s === 'SETTLED' ? 'rgba(34,197,94,0.15)'
  : s === 'RELEASED' ? 'rgba(148,163,184,0.15)'
  : 'rgba(148,163,184,0.10)';

function StatusPill({ status }: { status: string }) {
  return (
    <span style={{
      fontSize: 10, padding: '2px 8px', borderRadius: 8, fontWeight: 600,
      background: statusBg(status), color: statusColor(status),
    }}>{status}</span>
  );
}

// ── Component ─────────────────────────────────────────────

export default function Step3Tracker() {
  const [tab, setTab] = useState<'vc' | 'rl' | 'cps'>('vc');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const [vcRows, setVcRows] = useState<VCContractRow[]>([]);
  const [refundRows, setRefundRows] = useState<RefundLiability[]>([]);
  const [cpsRows, setCpsRows] = useState<CPSRow[]>([]);
  const [contractMap, setContractMap] = useState<Record<number, ContractSlim>>({});

  const loadContractMap = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/contracts`);
      if (res.ok) {
        const data = await res.json();
        const list: ContractSlim[] = data.contracts || data || [];
        const map: Record<number, ContractSlim> = {};
        for (const c of list) map[c.id] = c;
        setContractMap(map);
      }
    } catch { /* non-fatal — tracker renders without customer names */ }
  }, []);

  const loadVC = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const cRes = await authFetch(`${API}/contracts`);
      if (!cRes.ok) throw new Error(`HTTP ${cRes.status}`);
      const cData = await cRes.json();
      const contracts: ContractSlim[] = cData.contracts || cData || [];
      const rows: VCContractRow[] = [];
      for (const c of contracts) {
        try {
          const r = await authFetch(`${API}/contracts/${c.id}/variable-consideration-result`);
          if (!r.ok) continue;
          const d = await r.json();
          if (d.has_components) rows.push(d);
        } catch { /* skip contract on failure — other rows still render */ }
      }
      setVcRows(rows);
    } catch (e: any) {
      setError(e?.message || 'Failed to load variable consideration');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRefunds = useCallback(async (status?: string) => {
    setLoading(true);
    setError(null);
    try {
      const url = status
        ? `${API}/refund-liabilities?status=${status}`
        : `${API}/refund-liabilities`;
      const res = await authFetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRefundRows(data.liabilities || []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load refund liabilities');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCps = useCallback(async (status?: string) => {
    setLoading(true);
    setError(null);
    try {
      const url = status
        ? `${API}/cps?status=${status}`
        : `${API}/cps`;
      const res = await authFetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setCpsRows(data.payments || []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load CPS');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadContractMap(); }, [loadContractMap]);

  useEffect(() => {
    if (tab === 'vc') loadVC();
    else if (tab === 'rl') loadRefunds(statusFilter);
    else if (tab === 'cps') loadCps(statusFilter);
  }, [tab, statusFilter, loadVC, loadRefunds, loadCps]);

  const flashAndReload = (msg: string) => {
    setFlash(msg);
    setTimeout(() => setFlash(null), 3000);
    if (tab === 'rl') loadRefunds(statusFilter);
    else if (tab === 'cps') loadCps(statusFilter);
  };

  // ── Action handlers ──

  const reassessContract = async (contractId: number) => {
    try {
      const res = await authFetch(`${API}/contracts/${contractId}/assess-variable-consideration`, {
        method: 'POST',
      });
      if (res.ok) flashAndReload(`Contract ${contractId}: re-assessment complete`);
      else if (res.status === 409) {
        const body = await res.json();
        setError(body?.detail?.message || 'Tenant configuration required (409)');
      } else {
        setError(`Re-assess failed (HTTP ${res.status})`);
      }
      if (tab === 'vc') loadVC();
    } catch (e: any) {
      setError(e?.message || 'Network error');
    }
  };

  const rlAction = async (id: number, action: 'accrue' | 'settle' | 'release', body?: any) => {
    try {
      const res = await authFetch(`${API}/refund-liabilities/${id}/${action}`, {
        method: 'POST',
        headers: body ? { 'Content-Type': 'application/json' } : undefined,
        body: body ? JSON.stringify(body) : undefined,
      });
      if (res.ok) flashAndReload(`Refund liability #${id}: ${action}`);
      else {
        const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
        setError(err.detail || `HTTP ${res.status}`);
      }
    } catch (e: any) {
      setError(e?.message || 'Network error');
    }
  };

  const cpsAction = async (id: number, action: 'accrue' | 'settle' | 'release', body?: any) => {
    try {
      const res = await authFetch(`${API}/cps/${id}/${action}`, {
        method: 'POST',
        headers: body ? { 'Content-Type': 'application/json' } : undefined,
        body: body ? JSON.stringify(body) : undefined,
      });
      if (res.ok) flashAndReload(`CPS #${id}: ${action}`);
      else {
        const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
        setError(err.detail || `HTTP ${res.status}`);
      }
    } catch (e: any) {
      setError(e?.message || 'Network error');
    }
  };

  // ── KPIs ──

  const vcKpis = {
    contracts_with_vc: vcRows.length,
    total_components: vcRows.reduce((s, r) => s + r.components.length, 0),
    pending_adjustment_total: vcRows.reduce(
      (s, r) => s + r.components.reduce((s2, c) => s2 + (c.pending_adjustment_amount || 0), 0),
      0,
    ),
    unquantified_count: vcRows.filter(r => r.has_unquantified_components).length,
  };
  const rlKpis = {
    total: refundRows.length,
    open: refundRows.filter(r => r.status === 'OPEN').length,
    accrued: refundRows.filter(r => r.status === 'ACCRUED').length,
    outstanding_balance: refundRows.reduce((s, r) => s + (r.liability_balance || 0), 0),
  };
  const cpsKpis = {
    total: cpsRows.length,
    open: cpsRows.filter(r => r.status === 'OPEN').length,
    accrued: cpsRows.filter(r => r.status === 'ACCRUED').length,
    outstanding_balance: cpsRows.reduce((s, r) => s + (r.balance_due || 0), 0),
  };

  // ── Columns ──

  const rlCols: Column[] = [
    { key: 'id', label: 'ID', render: (v: number) => <span style={{ fontWeight: 600 }}>#{v}</span> },
    {
      key: 'revenue_contract_id', label: 'Contract',
      render: (v: number) => contractMap[v]?.contract_number || `#${v}`,
    },
    { key: 'entity_code', label: 'Entity' },
    { key: 'status', label: 'Status', render: (v: string) => <StatusPill status={v} /> },
    { key: 'initial_amount', label: 'Initial', align: 'right', render: (v: number) => formatAmount(v) },
    {
      key: 'liability_balance', label: 'Balance', align: 'right',
      render: (v: number) => <span style={{ color: v > 0 ? 'var(--warning)' : 'var(--text-secondary)', fontWeight: 600 }}>
        {formatAmount(v)}
      </span>,
    },
    {
      key: 'id', label: 'Actions', align: 'right',
      render: (_v: any, row: RefundLiability) => (
        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
          {row.status === 'OPEN' && (
            <button type="button" onClick={() => rlAction(row.id, 'accrue')}
              style={btnAccent}>Accrue</button>
          )}
          {row.status === 'ACCRUED' && (
            <button type="button"
              onClick={() => {
                const a = prompt(`Settle amount (balance ${formatAmount(row.liability_balance)}):`);
                if (a) rlAction(row.id, 'settle', { amount: parseFloat(a) });
              }}
              style={btnPositive}>Settle</button>
          )}
          {(row.status === 'OPEN' || row.status === 'ACCRUED') && (
            <button type="button"
              onClick={() => {
                const r = prompt('Release reason:');
                if (r) rlAction(row.id, 'release', { reason: r });
              }}
              style={btnMuted}>Release</button>
          )}
        </div>
      ),
    },
  ];

  const cpsCols: Column[] = [
    { key: 'id', label: 'ID', render: (v: number) => <span style={{ fontWeight: 600 }}>#{v}</span> },
    {
      key: 'revenue_contract_id', label: 'Contract',
      render: (v: number) => contractMap[v]?.contract_number || `#${v}`,
    },
    { key: 'entity_code', label: 'Entity' },
    { key: 'payment_type', label: 'Type' },
    {
      key: 'classification', label: 'Branch',
      render: (v: string) => <span style={{ fontSize: 10 }}>{v === 'DISTINCT_SERVICE_RECEIVED' ? '32-26(a)' : '32-26(b)'}</span>,
    },
    { key: 'status', label: 'Status', render: (v: string) => <StatusPill status={v} /> },
    { key: 'gross_amount', label: 'Gross', align: 'right', render: (v: number) => formatAmount(v) },
    {
      key: 'balance_due', label: 'Balance', align: 'right',
      render: (v: number) => <span style={{ color: v > 0 ? 'var(--warning)' : 'var(--text-secondary)', fontWeight: 600 }}>
        {formatAmount(v)}
      </span>,
    },
    {
      key: 'id', label: 'Actions', align: 'right',
      render: (_v: any, row: CPSRow) => (
        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
          {row.status === 'OPEN' && (
            <button type="button" onClick={() => cpsAction(row.id, 'accrue')}
              style={btnAccent}>Accrue</button>
          )}
          {row.status === 'ACCRUED' && (
            <button type="button"
              onClick={() => {
                const a = prompt(`Settle amount (balance ${formatAmount(row.balance_due)}):`);
                if (a) cpsAction(row.id, 'settle', { amount: parseFloat(a) });
              }}
              style={btnPositive}>Settle</button>
          )}
          {(row.status === 'OPEN' || row.status === 'ACCRUED') && (
            <button type="button"
              onClick={() => {
                const r = prompt('Release reason:');
                if (r) cpsAction(row.id, 'release', { reason: r });
              }}
              style={btnMuted}>Release</button>
          )}
        </div>
      ),
    },
  ];

  return (
    <PageLayout
      title="Step 3 Tracker"
      subtitle="ASC 606 Step 3 — variable consideration, refund liabilities, and consideration payable to customer across all contracts"
    >
      <TabBar
        tabs={[
          { key: 'vc', label: 'Variable Consideration' },
          { key: 'rl', label: 'Refund Liabilities' },
          { key: 'cps', label: 'CPS (32-26)' },
        ]}
        active={tab}
        onChange={(k) => { setTab(k as any); setStatusFilter(''); }}
      />

      {flash && (
        <div style={{ padding: 8, marginTop: 12, background: 'rgba(34,197,94,0.10)', color: 'var(--positive)', borderRadius: 4 }}>
          {flash}
        </div>
      )}
      {error && (
        <div style={{ padding: 8, marginTop: 12, background: 'rgba(239,68,68,0.10)', color: 'var(--negative)', borderRadius: 4 }}>
          {error}
        </div>
      )}

      {tab === 'vc' && (
        <>
          <div style={{ marginTop: 16 }}>
            <KpiGrid
              items={[
                { label: 'Contracts with VC', value: String(vcKpis.contracts_with_vc) },
                { label: 'Total Components', value: String(vcKpis.total_components) },
                {
                  label: 'Pending Adjustment',
                  value: formatAmountCompact(vcKpis.pending_adjustment_total),
                  color: vcKpis.pending_adjustment_total !== 0 ? 'var(--warning)' : undefined,
                },
                {
                  label: 'Unquantified (block approve)',
                  value: String(vcKpis.unquantified_count),
                  color: vcKpis.unquantified_count > 0 ? 'var(--negative)' : undefined,
                },
              ]}
            />
          </div>
          <div style={{ marginTop: 16 }}>
            {loading && <div style={{ color: 'var(--text-hint)' }}>Loading…</div>}
            {!loading && vcRows.length === 0 && (
              <div style={{ padding: 24, color: 'var(--text-hint)', fontStyle: 'italic' }}>
                No contracts currently have active variable consideration components.
              </div>
            )}
            {vcRows.map(row => (
              <div key={row.contract_id} style={{
                padding: 12, marginBottom: 10,
                background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 6,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div>
                    <strong>{row.contract_number}</strong>
                    <span style={{ marginLeft: 12, color: 'var(--text-secondary)', fontSize: 12 }}>
                      {contractMap[row.contract_id]?.customer_name || ''}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                      Aggregate: <strong style={{
                        color: row.variable_consideration_aggregate >= 0 ? 'var(--positive)' : 'var(--negative)',
                      }}>{formatAmount(row.variable_consideration_aggregate)}</strong>
                    </span>
                    <button type="button" onClick={() => reassessContract(row.contract_id)} style={btnAccent}>
                      Re-assess
                    </button>
                  </div>
                </div>
                <div>
                  {row.components.map(c => (
                    <div key={c.id} style={{
                      padding: 6, marginBottom: 4, fontSize: 11,
                      background: 'var(--bg-deep)', border: '1px solid var(--border-subtle)', borderRadius: 4,
                    }}>
                      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 600 }}>{c.component_type}</span>
                        {c.constraint_applied && (
                          <span style={{ fontSize: 10, color: 'var(--warning)', fontWeight: 600 }}>32-11 constrained</span>
                        )}
                        <span>est={formatAmount(c.estimated_amount)}</span>
                        {c.probability !== null && <span>p={(c.probability * 100).toFixed(0)}%</span>}
                        <span>incl={formatAmount(c.constrained_amount)}</span>
                        {c.pending_adjustment_amount !== 0 && (
                          <span style={{ color: 'var(--warning)', fontWeight: 600 }}>
                            pending Δ={formatAmount(c.pending_adjustment_amount)}
                          </span>
                        )}
                      </div>
                      {c.rationale && (
                        <div style={{ color: 'var(--text-hint)', fontStyle: 'italic', marginTop: 2 }}>{c.rationale}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {tab === 'rl' && (
        <>
          <div style={{ marginTop: 16 }}>
            <KpiGrid items={[
              { label: 'Total', value: String(rlKpis.total) },
              { label: 'Open', value: String(rlKpis.open), color: rlKpis.open > 0 ? 'var(--warning)' : undefined },
              { label: 'Accrued', value: String(rlKpis.accrued) },
              { label: 'Outstanding', value: formatAmountCompact(rlKpis.outstanding_balance) },
            ]} />
          </div>
          <StatusFilter value={statusFilter} onChange={setStatusFilter} />
          <DataTable columns={rlCols} rows={refundRows} loading={loading} emptyMessage="No refund liabilities." />
        </>
      )}

      {tab === 'cps' && (
        <>
          <div style={{ marginTop: 16 }}>
            <KpiGrid items={[
              { label: 'Total', value: String(cpsKpis.total) },
              { label: 'Open', value: String(cpsKpis.open), color: cpsKpis.open > 0 ? 'var(--warning)' : undefined },
              { label: 'Accrued', value: String(cpsKpis.accrued) },
              { label: 'Outstanding', value: formatAmountCompact(cpsKpis.outstanding_balance) },
            ]} />
          </div>
          <StatusFilter value={statusFilter} onChange={setStatusFilter} />
          <DataTable columns={cpsCols} rows={cpsRows} loading={loading} emptyMessage="No CPS rows." />
        </>
      )}
    </PageLayout>
  );
}

function StatusFilter({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div style={{ marginTop: 16, marginBottom: 8, display: 'flex', gap: 6, alignItems: 'center' }}>
      <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Status:</span>
      {(['', 'OPEN', 'ACCRUED', 'SETTLED', 'RELEASED'] as const).map(s => (
        <button
          key={s || 'all'}
          type="button"
          onClick={() => onChange(s)}
          style={{
            padding: '3px 10px', fontSize: 11,
            background: value === s ? 'var(--accent)' : 'var(--bg-surface)',
            color: value === s ? 'white' : 'var(--text-primary)',
            border: '1px solid var(--border-subtle)', borderRadius: 4, cursor: 'pointer',
          }}
        >{s || 'All'}</button>
      ))}
    </div>
  );
}

const btnAccent: React.CSSProperties = {
  padding: '3px 10px', fontSize: 10, fontWeight: 600,
  background: 'var(--accent)', color: 'white',
  border: 'none', borderRadius: 3, cursor: 'pointer',
};
const btnPositive: React.CSSProperties = {
  padding: '3px 10px', fontSize: 10, fontWeight: 600,
  background: 'var(--positive)', color: 'white',
  border: 'none', borderRadius: 3, cursor: 'pointer',
};
const btnMuted: React.CSSProperties = {
  padding: '3px 10px', fontSize: 10,
  background: 'transparent', color: 'var(--text-secondary)',
  border: '1px solid var(--border-subtle)', borderRadius: 3, cursor: 'pointer',
};
